import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { useMediaQuery } from "@base-ui/react/unstable-use-media-query";
import { Menu, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from "lucide-react";
import { Button } from "../Button";
import { Drawer } from "../Drawer";
import { OverlayContainerContext } from "../Dialog";
import { Tooltip } from "../Tooltip";

interface WorkspaceContextEntry {
    owner: string;
    content: ReactNode;
    label: string;
}

interface WorkspaceContextController {
    openContext: (owner: string, content: ReactNode, label?: string) => void;
    closeContext: (owner?: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextController | null>(null);

// Match the Attend AAA shell's fit calculation: keep the contextual rail as
// an inset card whenever it and a usable center can coexist. If all three
// columns do not fit, the left navigation becomes a drawer before the context
// rail is allowed to become an overlay.
const INLINE_LEFT_WIDTH = 260;
const INLINE_CONTEXT_DEFAULT_WIDTH = 296;
const INLINE_CONTEXT_MIN_WIDTH = 280;
const INLINE_CONTEXT_MAX_WIDTH = 560;
const INLINE_CONTEXT_KEYBOARD_STEP = 16;
const INLINE_CENTER_MIN = 320;
const WORKSPACE_GAP = 10;
const WORKSPACE_INLINE_PADDING = 20;
const MOBILE_SHELL_MAX = 760;
const CONTEXT_WIDTH_STORAGE_KEY = "crm-context-width";

function clampContextWidth(width: number, maximum = INLINE_CONTEXT_MAX_WIDTH) {
    return Math.min(Math.max(width, INLINE_CONTEXT_MIN_WIDTH), maximum);
}

function storedContextWidth() {
    const stored = Number.parseFloat(localStorage.getItem(CONTEXT_WIDTH_STORAGE_KEY) ?? "");
    return Number.isFinite(stored) ? clampContextWidth(stored) : INLINE_CONTEXT_DEFAULT_WIDTH;
}

/** Opens records in the shell's independent right-hand context rail. */
export function useWorkspaceContext() {
    const value = useContext(WorkspaceContext);
    if (!value) throw new Error("useWorkspaceContext must be used inside WorkspaceShell");
    return value;
}

export function WorkspaceShell({
    sidebar,
    mobileSidebar,
    header,
    children,
    contextRail,
    contextLabel = "context panel",
}: {
    sidebar: ReactNode;
    mobileSidebar: ReactNode | ((close: () => void) => ReactNode);
    header: ReactNode;
    children: ReactNode;
    contextRail?: ReactNode;
    contextLabel?: string;
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem("crm-sidebar-open") !== "false");
    const [compactNavigation, setCompactNavigation] = useState(() => matchMedia("(max-width: 760px)").matches);
    const [contextOpen, setContextOpen] = useState(() => localStorage.getItem("crm-context-open") !== "false");
    const phone = useMediaQuery("(max-width: 640px)", { noSsr: true });
    const [contextDrawerOpen, setContextDrawerOpen] = useState(false);
    const [dynamicContext, setDynamicContext] = useState<WorkspaceContextEntry | null>(null);
    const [shellWidth, setShellWidth] = useState(0);
    const [preferredContextWidth, setPreferredContextWidth] = useState(storedContextWidth);
    const contextOwnerRef = useRef<string | null>(null);
    const contextResizeRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
    const shellRef = useRef<HTMLDivElement | null>(null);
    const headerRef = useRef<HTMLElement | null>(null);
    const overlayContainerRef = useRef<HTMLDivElement | null>(null);
    function setSidebar(open: boolean) {
        setSidebarOpen(open);
        localStorage.setItem("crm-sidebar-open", String(open));
    }
    function setRail(open: boolean) {
        setContextOpen(open);
        localStorage.setItem("crm-context-open", String(open));
    }
    const openContext = useCallback((owner: string, content: ReactNode, label = "context panel") => {
        contextOwnerRef.current = owner;
        setDynamicContext({ owner, content, label });
        setContextOpen(true);
        localStorage.setItem("crm-context-open", "true");
    }, []);
    const closeContext = useCallback((owner?: string) => {
        if (owner && contextOwnerRef.current !== owner) return;
        contextOwnerRef.current = null;
        setDynamicContext(null);
        setContextDrawerOpen(false);
    }, []);
    const contextController = useMemo(() => ({ openContext, closeContext }), [openContext, closeContext]);
    const activeContextRail = dynamicContext?.content ?? contextRail;
    const activeContextLabel = dynamicContext?.label ?? contextLabel;
    const maximumContextWidth = shellWidth > 0
        ? Math.max(INLINE_CONTEXT_MIN_WIDTH, Math.min(
            INLINE_CONTEXT_MAX_WIDTH,
            shellWidth - INLINE_CENTER_MIN - WORKSPACE_GAP - WORKSPACE_INLINE_PADDING,
        ))
        : INLINE_CONTEXT_MAX_WIDTH;
    const contextWidth = clampContextWidth(preferredContextWidth, maximumContextWidth);
    const minimumInsetWidth = INLINE_CENTER_MIN + INLINE_CONTEXT_MIN_WIDTH + WORKSPACE_GAP + WORKSPACE_INLINE_PADDING;
    const minimumThreeColumnWidth = INLINE_LEFT_WIDTH + INLINE_CENTER_MIN + contextWidth + WORKSPACE_GAP * 2 + WORKSPACE_INLINE_PADDING;
    const canInsetContext = shellWidth === 0 || (shellWidth > MOBILE_SHELL_MAX && shellWidth >= minimumInsetWidth);
    const contextInset = Boolean(activeContextRail && contextOpen && canInsetContext);
    const autoHideSidebar = Boolean(contextInset && sidebarOpen && shellWidth > 0 && shellWidth < minimumThreeColumnWidth);
    const sidebarInlineOpen = sidebarOpen && !autoHideSidebar;
    const shellStyle = { "--context-rail-width": `${contextWidth}px` } as CSSProperties;

    function setContextWidth(width: number) {
        const next = clampContextWidth(width, maximumContextWidth);
        setPreferredContextWidth(next);
        localStorage.setItem(CONTEXT_WIDTH_STORAGE_KEY, String(next));
    }

    function startContextResize(event: PointerEvent<HTMLDivElement>) {
        if (event.button !== 0) return;
        contextResizeRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startWidth: contextWidth,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        document.documentElement.classList.add("context-rail-resizing");
    }

    function resizeContext(event: PointerEvent<HTMLDivElement>) {
        const resize = contextResizeRef.current;
        if (!resize || resize.pointerId !== event.pointerId) return;
        setContextWidth(resize.startWidth + resize.startX - event.clientX);
    }

    function stopContextResize(event: PointerEvent<HTMLDivElement>) {
        const resize = contextResizeRef.current;
        if (!resize || resize.pointerId !== event.pointerId) return;
        contextResizeRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        document.documentElement.classList.remove("context-rail-resizing");
    }

    function resizeContextWithKeyboard(event: KeyboardEvent<HTMLDivElement>) {
        let next: number | null = null;
        if (event.key === "ArrowLeft") next = contextWidth + INLINE_CONTEXT_KEYBOARD_STEP;
        if (event.key === "ArrowRight") next = contextWidth - INLINE_CONTEXT_KEYBOARD_STEP;
        if (event.key === "Home") next = INLINE_CONTEXT_MIN_WIDTH;
        if (event.key === "End") next = maximumContextWidth;
        if (next === null) return;
        event.preventDefault();
        setContextWidth(next);
    }

    useLayoutEffect(() => {
        const shell = shellRef.current;
        if (!shell) return;
        const measure = () => setShellWidth(shell.clientWidth);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(shell);
        return () => observer.disconnect();
    }, []);

    useLayoutEffect(() => () => {
        document.documentElement.classList.remove("context-rail-resizing");
    }, []);

    // Dynamic targets open inline whenever possible. Only fall back to the
    // drawer/bottom sheet when even a 320px center plus the rail cannot fit.
    useLayoutEffect(() => {
        if (!dynamicContext) return;
        setContextDrawerOpen(!canInsetContext);
    }, [canInsetContext, dynamicContext]);

    useLayoutEffect(() => {
        const headerElement = headerRef.current;
        if (!headerElement) return;

        const updateNavigationMode = () => {
            const brand = headerElement.querySelector<HTMLElement>(".menubar-brand");
            const menubar = headerElement.querySelector<HTMLElement>(".workspace-menubar");
            const actions = headerElement.querySelector<HTMLElement>(".header-actions");
            const contextToggle = headerElement.querySelector<HTMLElement>(".context-toggle");
            if (!brand || !menubar) return;

            const style = getComputedStyle(headerElement);
            const horizontalPadding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
            const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
            const visibleItemCount = 4 + (actions ? 1 : 0) + (contextToggle ? 1 : 0);
            const fixedWidth = 34
                + brand.scrollWidth
                + menubar.scrollWidth
                + (actions?.scrollWidth ?? 0)
                + (contextToggle?.scrollWidth ?? 0);
            // Keep enough room for search to remain useful. If the complete
            // menubar cannot coexist with that, the drawer is the clearer UI.
            const minimumSearchWidth = 240;
            const requiredWidth = horizontalPadding + fixedWidth + minimumSearchWidth + gap * (visibleItemCount - 1);
            setCompactNavigation(headerElement.clientWidth < requiredWidth);
        };

        updateNavigationMode();
        const observer = new ResizeObserver(updateNavigationMode);
        observer.observe(headerElement);
        return () => observer.disconnect();
    }, [activeContextRail]);

    return (
        <WorkspaceContext.Provider value={contextController}>
        <OverlayContainerContext.Provider value={overlayContainerRef}>
        <Drawer.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
            <div ref={shellRef} style={shellStyle} className={`workspace-shell${contextInset ? " has-context context-inset" : ""}${!sidebarInlineOpen ? " sidebar-closed" : ""}${autoHideSidebar ? " sidebar-auto-hidden" : ""}${compactNavigation ? " navigation-collapsed" : ""}`}>
                <header className="workspace-header" ref={headerRef}>
                    {compactNavigation || autoHideSidebar ? (
                        <Drawer.Trigger render={<Button variant="ghost" className="mobile-menu" aria-label="Open navigation" />}><Menu /></Drawer.Trigger>
                    ) : (
                        <Tooltip.Root>
                            <Tooltip.Trigger render={<Button variant="ghost" size="sm" className="sidebar-toggle" aria-label={`${sidebarOpen ? "Hide" : "Show"} navigation sidebar`} aria-pressed={!sidebarOpen} onClick={() => setSidebar(!sidebarOpen)} />}>
                                {sidebarOpen ? <PanelLeftClose /> : <PanelLeftOpen />}
                            </Tooltip.Trigger>
                            <Tooltip.Content>{sidebarOpen ? "Hide" : "Show"} navigation sidebar</Tooltip.Content>
                        </Tooltip.Root>
                    )}
                    {header}
                    {activeContextRail && (
                        <Tooltip.Root>
                            <Tooltip.Trigger render={<Button variant="ghost" size="sm" className="context-toggle" aria-label={`${canInsetContext ? contextOpen ? "Hide" : "Show" : "Open"} ${activeContextLabel}`} onClick={() => canInsetContext ? setRail(!contextOpen) : setContextDrawerOpen(true)} />}>
                                {canInsetContext && contextOpen ? <PanelRightClose /> : <PanelRightOpen />}
                            </Tooltip.Trigger>
                            <Tooltip.Content>{canInsetContext ? contextOpen ? "Hide" : "Show" : "Open"} {activeContextLabel}</Tooltip.Content>
                        </Tooltip.Root>
                    )}
                </header>
                <div className="workspace-body">
                    <aside className="floating-sidebar">{sidebar}</aside>
                    <div className="workspace-center" ref={overlayContainerRef}>
                        <main className="main">{children}</main>
                    </div>
                    {contextInset && (
                        <div className="context-rail-slot">
                            <div
                                className="context-rail-resizer"
                                role="separator"
                                tabIndex={0}
                                aria-label="Resize context sidebar"
                                aria-orientation="vertical"
                                aria-valuemin={INLINE_CONTEXT_MIN_WIDTH}
                                aria-valuemax={maximumContextWidth}
                                aria-valuenow={Math.round(contextWidth)}
                                onPointerDown={startContextResize}
                                onPointerMove={resizeContext}
                                onPointerUp={stopContextResize}
                                onPointerCancel={stopContextResize}
                                onKeyDown={resizeContextWithKeyboard}
                            />
                            {activeContextRail}
                        </div>
                    )}
                </div>
            </div>
            <Drawer.Panel side="left" className="mobile-focus-sidebar" aria-label="Main navigation">
                {typeof mobileSidebar === "function" ? mobileSidebar(() => setDrawerOpen(false)) : mobileSidebar}
            </Drawer.Panel>
        </Drawer.Root>
        <Drawer.Root open={contextDrawerOpen} onOpenChange={setContextDrawerOpen}>
            <Drawer.Panel side={phone ? "bottom" : "right"} className="mobile-context-rail" aria-label={activeContextLabel}>
                {activeContextRail}
            </Drawer.Panel>
        </Drawer.Root>
        </OverlayContainerContext.Provider>
        </WorkspaceContext.Provider>
    );
}
